import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Button,
  Drawer,
  Select,
  Textarea,
  TimeField,
} from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { errorMessages } from "./staffingFormatters";
import {
  addDays,
  drawerDayLabel,
  hoursLabel,
  moneyLabel,
  rateLabel,
  shiftDay,
  shiftMinutes,
} from "./scheduleFormatters";
import { useStaffingScheduleContext } from "./StaffingScheduleContext";

const BREAK_OPTIONS = [
  { value: 0, label: "None" },
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 45, label: "45 min" },
  { value: 60, label: "60 min" },
];

const OPEN_SHIFT = "";
const PREVIEW_DELAY_MS = 300;

const initialForm = (shift) => ({
  staff_position_id: shift.staff_position_id,
  staff_member_id: shift.staff_member_id ?? OPEN_SHIFT,
  calendar_event_id: shift.calendar_event_id,
  room_id: shift.room_id ?? null,
  start_clock: shift.start_clock,
  end_clock: shift.end_clock,
  unpaid_break_minutes: shift.unpaid_break_minutes ?? 0,
  notes: shift.notes ?? "",
});

const ShiftDrawer = ({ shift, onClose, onSaved }) => {
  const { schedule, permissions, paths, venueOwnershipId } =
    useStaffingScheduleContext();
  const isNew = !shift.id;
  const editable = isNew ? permissions.manageSchedule : shift.can_edit;
  const [form, setForm] = useState(() => initialForm(shift));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [preview, setPreview] = useState(null);
  const previewRequest = useRef(0);
  const setField = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  const columnsById = useMemo(
    () =>
      Object.fromEntries(schedule.columns.map((column) => [column.id, column])),
    [schedule.columns],
  );
  const event = columnsById[form.calendar_event_id];
  const keepsOwnDate =
    shift.work_date && form.calendar_event_id === shift.calendar_event_id;
  // The drawer works in the schedule's 5am business day; a shift with no show is stored by its calendar date.
  const workDate =
    keepsOwnDate || !event
      ? isNew
        ? shift.work_date
        : shiftDay(shift)
      : event.date;
  const savedWorkDate =
    !form.calendar_event_id && form.start_clock < "05:00"
      ? addDays(workDate, 1)
      : workDate;

  const positionOptions = schedule.rows
    .filter(
      (row) =>
        !editable || row.can_manage || row.id === shift.staff_position_id,
    )
    .map((row) => {
      const rate = row.volunteer
        ? "Volunteer"
        : row.default_hourly_rate && rateLabel(row.default_hourly_rate);
      return {
        value: row.id,
        label: rate ? `${row.name} · ${rate}` : row.name,
      };
    });

  const staffOptions = useMemo(() => {
    const people = schedule.staffOptions[form.staff_position_id] || [];
    const options = people.map((person) => ({
      value: person.id,
      label: `${person.name} · ${hoursLabel(person.scheduled_minutes)} h`,
    }));
    if (
      shift.staff_member_id &&
      !people.some((person) => person.id === shift.staff_member_id)
    ) {
      options.unshift({
        value: shift.staff_member_id,
        label:
          `${shift.staff_member_first_name} ${shift.staff_member_last_name || ""}`.trim(),
      });
    }
    return [
      { value: OPEN_SHIFT, label: "Open shift (anyone eligible can claim)" },
      ...options,
    ];
  }, [schedule.staffOptions, form.staff_position_id, shift]);

  const eventOptions = schedule.columns
    .filter(
      (column) =>
        !column.day &&
        (column.date === workDate || column.id === shift.calendar_event_id),
    )
    .map((column) => ({
      value: column.id,
      label: [column.title, ...column.room_names].join(" · "),
    }));

  // Only a show across several rooms asks which room; a one-room show places the shift itself.
  const roomOptions =
    event?.room_ids.length > 1
      ? event.room_ids.map((id, index) => ({
          value: id,
          label: event.room_names[index],
        }))
      : [];

  const title = isNew
    ? "New shift"
    : shift.staff_member_id
      ? `${shift.staff_member_first_name} ${shift.staff_member_last_name || ""}`.trim()
      : "Open shift";
  const subtitle = [event?.title, drawerDayLabel(workDate)]
    .filter(Boolean)
    .join(" · ");
  const minutes = shiftMinutes(
    form.start_clock,
    form.end_clock,
    form.unpaid_break_minutes,
  );
  const unchanged =
    !isNew && JSON.stringify(form) === JSON.stringify(initialForm(shift));
  const previewKey = JSON.stringify([form, workDate]);
  const cost = unchanged
    ? shift.scheduled_cost
    : preview?.key === previewKey
      ? preview.data.scheduled_cost
      : null;
  const warnings = preview
    ? preview.data.warnings
    : isNew
      ? []
      : shift.warnings;
  const templateAnchor = preview
    ? preview.data.template_anchor
    : shift.template_anchor;
  const footerSummary = [
    `${hoursLabel(minutes)} h`,
    cost != null ? moneyLabel(cost) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const published = !isNew && shift.state === "published";

  const request = async (send, successMessage) => {
    setSaving(true);
    setErrors([]);
    let response;
    try {
      response = await send();
    } catch (error) {
      setErrors(errorMessages(error));
      setSaving(false);
      toastError("Could not save this shift.");
      return;
    }
    setSaving(false);
    toastSuccess(successMessage);
    onSaved(response?.data || null);
  };

  const payload = () => ({
    shift: {
      venue_ownership_id: venueOwnershipId,
      staff_position_id: form.staff_position_id,
      staff_member_id:
        form.staff_member_id === OPEN_SHIFT ? null : form.staff_member_id,
      calendar_event_id: form.calendar_event_id || null,
      room_id: form.room_id,
      work_date: savedWorkDate,
      start_clock: form.start_clock,
      end_clock: form.end_clock,
      unpaid_break_minutes: form.unpaid_break_minutes,
      notes: form.notes,
    },
  });

  useEffect(() => {
    const request = ++previewRequest.current;
    if (!editable || !paths.shiftPreviews || unchanged) {
      setPreview(null);
      return undefined;
    }
    const key = previewKey;
    const timer = setTimeout(async () => {
      try {
        const response = await client.post(paths.shiftPreviews, {
          id: shift.id,
          ...payload(),
        });
        if (request === previewRequest.current)
          setPreview({ key, data: response.data });
      } catch (error) {
        if (
          request === previewRequest.current &&
          error.response?.status === 422
        )
          setPreview({
            key,
            data: { warnings: [], template_anchor: shift.template_anchor },
          });
      }
    }, PREVIEW_DELAY_MS);
    return () => {
      clearTimeout(timer);
      previewRequest.current += 1;
    };
  }, [previewKey]);

  const save = () =>
    request(
      () =>
        isNew
          ? client.post(paths.shifts, payload())
          : client.patch(`${paths.shifts}/${shift.id}`, payload()),
      published
        ? `Saved. Publish to notify ${shift.staff_member_first_name || "staff"}.`
        : "Draft saved.",
    );
  const destroy = () =>
    request(
      () => client.delete(`${paths.shifts}/${shift.id}`),
      published
        ? "Shift removed. Staff hear about it when you publish."
        : "Shift deleted.",
    );

  const footer = (
    <div className="d-flex w-100 align-items-center" style={{ gap: 8 }}>
      {!isNew && editable && (
        <Button variant="link-destructive" disabled={saving} onClick={destroy}>
          Delete
        </Button>
      )}
      <div className="ml-auto d-flex align-items-center" style={{ gap: 12 }}>
        <span
          style={{
            fontSize: 14,
            lineHeight: "20px",
            color: "var(--sl-text-tertiary)",
          }}
        >
          {footerSummary}
        </span>
        <Button variant="secondary" onClick={onClose}>
          {editable ? "Cancel" : "Close"}
        </Button>
        {editable && (
          <Button variant="primary" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Save draft"}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Drawer
      open
      backdrop
      width={440}
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      footer={footer}
    >
      {published && (
        <div className="mb-3">
          <Alert
            color="brand"
            description="Published shift. Edits stay pending until you publish again."
          />
        </div>
      )}
      {errors.length > 0 && (
        <div className="mb-3">
          <Alert
            color="error"
            title="Could not save this shift"
            description={errors.join(" ")}
          />
        </div>
      )}
      <div className="mb-3">
        <Select
          id="shift-position"
          label="Position"
          disabled={!editable}
          options={positionOptions}
          value={form.staff_position_id}
          onChange={(value) =>
            setForm((current) => ({
              ...current,
              staff_position_id: value,
              staff_member_id: OPEN_SHIFT,
            }))
          }
        />
      </div>
      <div className="mb-3">
        <Select
          id="shift-staff-member"
          label="Assigned to"
          hint="Hours shown are this week's scheduled total."
          disabled={!editable}
          options={staffOptions}
          value={form.staff_member_id}
          onChange={(value) => setField("staff_member_id", value ?? OPEN_SHIFT)}
        />
      </div>
      <div
        className="mb-3"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <TimeField
            id="shift-start"
            label="Start"
            disabled={!editable}
            value={form.start_clock}
            onChange={(value) => setField("start_clock", value)}
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <TimeField
            id="shift-end"
            label="End"
            disabled={!editable}
            value={form.end_clock}
            onChange={(value) => setField("end_clock", value)}
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <Select
            id="shift-unpaid-break"
            label="Unpaid break"
            disabled={!editable}
            options={BREAK_OPTIONS}
            value={Number(form.unpaid_break_minutes)}
            onChange={(value) => setField("unpaid_break_minutes", value)}
          />
        </div>
      </div>
      {templateAnchor && (
        <div
          className="mb-3"
          style={{
            fontSize: 12,
            lineHeight: "18px",
            color: "var(--sl-text-quaternary)",
          }}
        >
          {`Template anchor: ${templateAnchor}`}
        </div>
      )}
      <div className="mb-3">
        <Select
          id="shift-event"
          label="Event"
          clearable
          placeholder="Not tied to a show"
          disabled={!editable}
          options={eventOptions}
          value={form.calendar_event_id}
          onChange={(value) => {
            const next = columnsById[value];
            setForm((current) => ({
              ...current,
              calendar_event_id: value ?? null,
              room_id:
                value === shift.calendar_event_id
                  ? (shift.room_id ?? null)
                  : next?.room_ids.includes(current.room_id)
                    ? current.room_id
                    : next?.room_ids.length === 1
                      ? next.room_ids[0]
                      : null,
            }));
          }}
        />
      </div>
      {roomOptions.length > 0 && (
        <div className="mb-3">
          <Select
            id="shift-room"
            label="Room"
            placeholder="Choose a room"
            hint="This show uses more than one room. Staff see the room in the app."
            disabled={!editable}
            options={roomOptions}
            value={form.room_id}
            onChange={(value) => setField("room_id", value ?? null)}
          />
        </div>
      )}
      <div className="mb-3">
        <Textarea
          id="shift-notes"
          label="Notes for staff"
          placeholder="e.g. Work the balcony bar"
          disabled={!editable}
          value={form.notes}
          onChange={(event) => setField("notes", event.target.value)}
        />
      </div>
      {warnings?.length > 0 && (
        <Alert
          color="warning"
          title="Check before publishing"
          description={warnings.join(" ")}
        />
      )}
    </Drawer>
  );
};

ShiftDrawer.propTypes = {
  shift: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default ShiftDrawer;
